/**
 * Tiefe Fragen — Historie-Seite (2026-07-20, Martin: "eigener Button oder
 * Link in der Sidebar, wo die Fragen betrachtet werden koennen").
 *
 * Zeigt die Faeden: jede Tagesfrage (Wurzel) mit ihren Antworten und den
 * tieferen Ebenen darunter — der ganze Gang, den man gegangen ist. So wird
 * sichtbar, was das System ueber einen weiss und woraus es schoepft.
 */
import { hapticTap, unlockAudio } from "./audio-feedback.js";
import { clearPageHash, setPageHash } from "./page-routes.js";
import { antwortenDesTages, type TiefeFaden, type TiefeZeile } from "./tiefe-antwort.js";
import { wienTagSchluessel } from "./zuhause.js";
import { acquireDictationStream } from "./ptt.js";
import { LiveDictation } from "./live-dictation.js";

// Geraete-Telemetrie ins Server-Log — die Diktat-Ausfaelle passieren NUR
// auf Martins iPhone; diese Zeilen liefern die Wahrheit vom Geraet.
function dqDebug(line: string): void {
  try {
    void fetch("/api/debug/ptt-timing", {
      method: "POST",
      body: JSON.stringify({ line }),
    }).catch(() => undefined);
  } catch {
    /* Telemetrie ist Kuer */
  }
}
import { buildPageHeader } from "./sheet-ui.js";
import { createMicViz } from "./mic-visualizer.js";
import { hookHiFiPlayback, prewarmHiFi } from "./playback-hifi.js";
import { zeichen } from "./zeichen.js";
import { fadenStand, fadenEtikett } from "../../shared/faden-stand.js";

type Status = "offen" | "beantwortet" | "ausgewichen" | "gelandet";
/** Was am Faden steht — siehe shared/faden-stand.ts. */
type Etikett = "gelandet" | "beantwortet" | "unbeantwortet";

interface Row {
  id: string;
  question: string;
  answerText: string | null;
  status: Status;
  depth: number;
  askedOn: string;
  goal: string | null;
  theme: string | null;
  /** Der Satz, in dem der Gang gelandet ist — die Erkenntnis (07.09.2026). */
  landungText?: string | null;
}

interface Thread {
  root: Row;
  steps: Row[];
}

/*
 * ⚠️ 22.09.2026 — „ausgewichen" ist hier verschwunden. Es beschrieb
 *    Faeden, in denen schlicht nichts stand, als Abwendung. Begruendung
 *    und Zaehlung in shared/faden-stand.ts.
 */
const STATUS_LABEL: Record<Etikett, string> = {
  beantwortet: "beantwortet",
  gelandet: "gelandet",
  unbeantwortet: "nicht beantwortet",
};

function fmtDay(iso: string): string {
  // iso = "YYYY-MM-DD"
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  return `${m[3]}.${m[2]}.${m[1]}`;
}

function buildThreadCard(t: Thread, opts?: { open?: boolean }): HTMLElement {
  // 2026-07-20 (Martin, "uebersichtlich zum Aufklappen"): jeder Faden ist
  // ein zugeklapptes details — nur der Kopf (Datum, Tiefe, Status, Frage)
  // ist sichtbar; der juengste Faden startet offen.
  const card = document.createElement("details");
  card.className = "dq-thread";
  if (opts?.open) card.open = true;

  const head = document.createElement("summary");
  head.className = "dq-thread-head";
  const day = document.createElement("span");
  day.className = "dq-thread-day";
  day.textContent = fmtDay(t.root.askedOn);
  head.appendChild(day);
  const tiefe = t.steps.filter((s) => s.answerText).length;
  if (tiefe > 1) {
    const badge = document.createElement("span");
    badge.className = "dq-thread-depth";
    badge.textContent = `${tiefe} Ebenen tief`;
    head.appendChild(badge);
  }
  /*
   * ⚠️ 08.09.2026 — hier stand der Zustand der LETZTEN Zeile.
   *
   * Martin: *„Bei ‚Tiefer Frage' steht immer noch nicht ‚Gelandet'. Bei den
   * anderen, wo ich gar nichts gemacht habe, steht ‚Gelandet'."* An seinen
   * Daten nachgezaehlt: 35 beantwortete Fragen ohne das Etikett, 17
   * unbeantwortete mit ihm. Am Ende eines Gangs haengt oft eine frisch
   * erzeugte, unbeantwortete Frage — und genau die wurde beim Schliessen
   * gelandet. Das Etikett beschrieb den leeren Nachzuegler.
   *
   * Die Regel steht jetzt in shared/faden-stand.ts und misst den GANZEN
   * Gang: der Schlusssatz beweist die Landung, die Antworten die Arbeit.
   */
  const stand = fadenStand(t.steps);
  const etikett = fadenEtikett(stand);
  if (etikett) {
    const st = document.createElement("span");
    st.className = `dq-status dq-status--${stand}`;
    st.textContent = STATUS_LABEL[stand];
    head.appendChild(st);
  }
  const preview = document.createElement("span");
  preview.className = "dq-thread-preview";
  preview.textContent = t.root.question;
  head.appendChild(preview);
  /*
   * 07.09.2026 — die Erkenntnis steht neben der Frage, nicht darin.
   *
   * Martin: *„Sogar die Weisheit daneben stehen, die Erkenntnis aus dem
   * Frageaspekt. Denn um die geht es ja schlussendlich."*
   *
   * ⚠️ Sie gehoert in den ZUGEKLAPPTEN Kopf. Eine Einsicht, die man erst
   *    aufklappen muss, ist beim Ueberfliegen der Liste nicht da — und die
   *    Liste ist genau der Ort, an dem er sie sucht.
   */
  const erkannt = [...t.steps].reverse().find((s) => s.landungText?.trim());
  if (erkannt?.landungText) {
    const weisheit = document.createElement("span");
    weisheit.className = "dq-thread-erkenntnis";
    weisheit.textContent = erkannt.landungText;
    head.appendChild(weisheit);
  }
  card.appendChild(head);

  for (const step of t.steps) {
    const q = document.createElement("p");
    q.className = "dq-q";
    if (step.depth > 0) q.classList.add("dq-q--deeper");
    q.textContent = step.question;
    card.appendChild(q);
    if (step.answerText) {
      const a = document.createElement("p");
      a.className = "dq-a";
      a.textContent = step.answerText;
      card.appendChild(a);
    }
  }
  // 2026-07-20 (Martin, "Frageablauf weiterfuehren, z.B. das mit
  // Bernhard"): jeder Faden mit mindestens einer Antwort laesst sich
  // spaeter fortsetzen — die naechste tiefere Frage entsteht aus dem
  // bisherigen Gang (gleiche /deeper-Mechanik wie im Live-Faden).
  const lastAnswered = [...t.steps].reverse().find((x) => x.answerText);
  // 2026-07-22 (Martin, "bei der Hauptfrage steht kein Button"): ein noch
  // unbeantworteter Faden (die offene Tagesfrage) bekommt einen direkten
  // Beantworten-Knopf — vorher gab es dort nichts zum Draufdruecken.
  if (!lastAnswered) {
    const row = document.createElement("div");
    row.className = "dq-continue-row";
    const antw = document.createElement("button");
    antw.type = "button";
    antw.className = "btn btn--sm btn--primary";
    antw.textContent = "Beantworten";
    antw.addEventListener("click", () => {
      openDeepQuestionSheet({
        id: t.root.id,
        question: t.root.question,
        depth: t.root.depth,
      });
    });
    row.appendChild(antw);
    card.appendChild(row);
  }
  if (lastAnswered) {
    const contRow = document.createElement("div");
    contRow.className = "dq-continue-row";
    const cont = document.createElement("button");
    cont.type = "button";
    cont.className = "btn btn--sm btn--primary";
    cont.textContent = "Weiterführen";
    cont.addEventListener("click", () => {
      cont.disabled = true;
      cont.textContent = "Die nächste Frage entsteht …";
      void (async () => {
        try {
          const j = await holeDeeper(lastAnswered.id);
          deeperPrefetch.delete(lastAnswered.id);
          if (j?.question?.id) {
            openDeepQuestionSheet(j.question);
          } else {
            reqDeps().showToast(
              "Die nächste Frage konnte gerade nicht entstehen — bitte gleich nochmal.",
              "error",
            );
          }
        } catch {
          reqDeps().showToast(
            "Die nächste Frage konnte gerade nicht entstehen — bitte gleich nochmal.",
            "error",
          );
        } finally {
          cont.disabled = false;
          cont.textContent = "Weiterführen";
        }
      })();
    });
    contRow.appendChild(cont);
    card.appendChild(contRow);
  }
  return card;
}

async function fillBody(body: HTMLElement): Promise<void> {
  body.replaceChildren();
  const load = document.createElement("div");
  load.className = "load-indicator";
  const spin = document.createElement("span");
  spin.className = "load-spinner";
  spin.setAttribute("aria-hidden", "true");
  load.appendChild(spin);
  body.appendChild(load);
  try {
    const r = await fetch("/api/deep-questions/history", {
      credentials: "include",
    });
    const j = (await r.json()) as { threads?: Thread[] };
    const threads = (j.threads ?? []).filter(
      (t) => t.steps.some((s) => s.answerText) || t.root.status === "offen",
    );
    body.replaceChildren();

    const intro = document.createElement("p");
    intro.className = "dq-intro";
    intro.textContent =
      "Jede Frage, die du beantwortet hast, formt still, was Vespera über dich weiß — der Chat, der Essenz-Spiegel und die nächsten Fragen schöpfen daraus.";
    body.appendChild(intro);

    // V4 (2026-07-20, Martin): aktives Fragespiel — eine frische Frage
    // holen, wenn gerade Lust und Raum da ist, statt auf den Morgen zu
    // warten. Existiert schon eine offene Frage, oeffnet sie sich direkt.
    const askRow = document.createElement("div");
    askRow.className = "dq-ask-row";
    const askBtn = document.createElement("button");
    askBtn.type = "button";
    askBtn.className = "btn btn--primary";
    askBtn.textContent = "Frag mich jetzt";
    askBtn.addEventListener("click", () => {
      askBtn.disabled = true;
      askBtn.textContent = "Eine Frage entsteht …";
      void (async () => {
        try {
          const r = await fetch("/api/deep-questions/ask", {
            method: "POST",
            credentials: "include",
          });
          const j = (await r.json()) as {
            question?: { id: string; question: string; depth?: number } | null;
          };
          if (j.question?.id) {
            openDeepQuestionSheet(j.question);
          } else {
            reqDeps().showToast(
              "Es kam gerade keine Frage zurück — bitte gleich nochmal.",
              "error",
            );
          }
        } catch {
          reqDeps().showToast(
            "Es kam gerade keine Frage zurück — bitte gleich nochmal.",
            "error",
          );
        } finally {
          askBtn.disabled = false;
          askBtn.textContent = "Frag mich jetzt";
        }
      })();
    });
    askRow.appendChild(askBtn);
    const askHint = document.createElement("span");
    askHint.className = "dq-ask-hint";
    askHint.textContent = "Wenn du gerade Lust hast, weiter vorzudringen.";
    askRow.appendChild(askHint);
    body.appendChild(askRow);

    if (threads.length === 0) {
      const empty = document.createElement("p");
      empty.className = "dq-empty";
      empty.textContent =
        "Noch keine beantworteten Fragen. Sobald du auf eine Tagesfrage antwortest, steht sie hier — mit dem ganzen Gang, den ihr gegangen seid.";
      body.appendChild(empty);
      return;
    }
    // 2026-07-20 (Martin, 2. Anlauf): ALLE Faeden zugeklappt — die
    // Urfrage steht immer im Kopf, aufklappen zeigt den ganzen Gang.
    threads.forEach((t) => body.appendChild(buildThreadCard(t)));
  } catch {
    body.replaceChildren();
    const err = document.createElement("p");
    err.className = "dq-empty";
    err.textContent =
      "Konnte die Fragen gerade nicht laden — bitte später erneut.";
    body.appendChild(err);
  }
}

export function openDeepQuestionsView(): void {
  if (document.getElementById("deep-questions-sheet")) return;
  /*
   * 08.09.2026 — der Hash fehlte, und damit war diese Seite als einzige der
   * sieben angemeldeten Routen NICHT neuladefest. Sie stand seit dem
   * 20.07. im Router (`key: "tiefe-fragen"`), aber niemand setzte den Hash:
   * `openPagesFromHash` fand beim Start nie etwas zu oeffnen.
   */
  setPageHash("tiefe-fragen");
  // Desktop: Sidebar sichtbar halten (zentral in app.ts behandelt).
  window.dispatchEvent(new CustomEvent("voice-v8:page-open"));
  const el = document.createElement("div");
  el.className = "sheet deep-questions-sheet page-sheet";
  el.id = "deep-questions-sheet";
  el.setAttribute("role", "dialog");
  el.setAttribute("aria-modal", "true");

  const cardWrap = document.createElement("div");
  cardWrap.className = "sheet-card";
  const body = document.createElement("div");
  body.className = "dq-body page-body";
  cardWrap.appendChild(body);
  el.appendChild(cardWrap);

  cardWrap.prepend(buildPageHeader("Tiefe Fragen", closeDeepQuestionsView));
  document.body.appendChild(el);
  /* 2026-08-30: Der eigene Aufruf ist entfallen — buildPageHeader meldet
     jede Seite mit geteiltem Kopf beim einen Wisch-Waechter an. Zwei
     Handler haetten denselben Rueckweg zweimal ausgeloest. */
  void fillBody(body);
}

export function closeDeepQuestionsView(): void {
  document.getElementById("deep-questions-sheet")?.remove();
  clearPageHash("tiefe-fragen");
}


// ── Tiefe-Frage-Antwort (Modal + Dialog + Banner) — 2026-07-20 aus app.ts
// herausgeloest (Martin: "modular, keine Schnipsel"). App-lokale UI-Helfer
// kommen ueber initDeepQuestions als Deps herein. ─────────────────────────
import { loadSettings as loadDeepqSettings } from "./settings.js";

interface DeepQuestionsDeps {
  showToast: (message: string, kind?: string) => void;
  showStatusBanner: (opts: {
    kind: "info" | "warning" | "error" | "success";
    message: string;
    actionLabel?: string;
    actionHandler?: () => void;
    owner?: "state" | "update";
    onDismiss?: () => void;
  }) => void;
  markDeepqBanner: () => void;
}

let deps: DeepQuestionsDeps | null = null;
export function initDeepQuestions(d: DeepQuestionsDeps): void {
  deps = d;
}
function reqDeps(): DeepQuestionsDeps {
  if (!deps) throw new Error("initDeepQuestions() nicht aufgerufen");
  return deps;
}

const DEEPQ_DISMISS_KEY = "voice-v8.deepq-dismissed";

// Tempo (Martin: "dauert ewig, bis die Frage auftaucht"): Nach dem
// Absenden einer Antwort wird die Folge-Frage im HINTERGRUND erzeugt —
// "Weiterfuehren" greift dann auf das laufende/fertige Ergebnis zu.
type DeeperAntwort = {
  question?: { id: string; question: string; depth?: number } | null;
  /**
   * Der Gang ist angekommen (2026-09-03). Statt einer weiteren Ebene ein
   * Satz, der zurueckgibt, wo Martin gelandet ist.
   */
  abschluss?: string | null;
} | null;
const deeperPrefetch = new Map<string, Promise<DeeperAntwort>>();

function holeDeeper(id: string): Promise<DeeperAntwort> {
  const laufend = deeperPrefetch.get(id);
  if (laufend) return laufend;
  const p = fetch(`/api/deep-questions/${encodeURIComponent(id)}/deeper`, {
    method: "POST",
    credentials: "include",
  })
    .then((r) => r.json() as Promise<DeeperAntwort>)
    .catch(() => null);
  deeperPrefetch.set(id, p);
  return p;
}

interface DeepQuestionOpen {
  id: string;
  question: string;
  goal?: string | null;
  /** V3 Tiefen-Faden: 0 = Tagesfrage, >0 = Folge-Ebene. */
  depth?: number;
}

export async function checkDeepQuestion(): Promise<void> {
  if (!deps) return;
  try {
    const r = await fetch("/api/deep-questions/open", {
      credentials: "include",
    });
    if (!r.ok) return;
    const j = (await r.json()) as { question?: DeepQuestionOpen | null };
    const q = j.question;
    const navBtn = document.getElementById("open-deepq-btn");
    if (!q?.id || !q.question) {
      navBtn?.classList.remove("deepq-open-dot");
      return;
    }
    // 2026-07-21 (Martin): stiller Punkt am Sidebar-Eintrag, solange eine
    // Frage offen ist — auch wenn der Banner weggeklickt wurde.
    navBtn?.classList.add("deepq-open-dot");
    const today = new Date().toISOString().slice(0, 10);
    if (localStorage.getItem(DEEPQ_DISMISS_KEY) === `${q.id}|${today}`) return;
    const bannerTitel =
      (q.depth ?? 0) > 0
        ? `Tiefen-Faden · Ebene ${(q.depth ?? 0) + 1}`
        : "Tiefe Frage des Tages";
    /*
     * ⚠️ 2026-08-30 — KEIN Banner mehr im Chat. Martins Entscheidung:
     *
     *   *„Die Frage ist jetzt wieder als Modal erschienen. Im Chatverlauf
     *   dachte ich, dass wir das wegmachen. Abgesehen davon habe ich schon
     *   siebzehn Ebenen beantwortet. Warum erscheint die Frage schon wieder?
     *   Sollte nicht einmal am Tag nur eine neue erscheinen?"*
     *
     * Er hat in beidem recht, und der zweite Punkt erklaert den ersten:
     * JEDE beantwortete Ebene erzeugt eine Folgefrage mit eigener Kennung.
     * Das Wegklicken merkte sich `id|heute` — also meldete sich der Banner
     * nach jeder Antwort erneut. An seinen Daten vom 30.08. gemessen: 16
     * beantwortete Ebenen, 16 offene Folgefragen, 16 moegliche Banner. Aus
     * „einmal am Tag" wurde „nach jeder Antwort".
     *
     * Die Frage wartet jetzt auf ZUHAUSE, in der Offen-Liste — dort, wo er
     * ohnehin hingeht, um Dinge abzuarbeiten. Der leise Punkt am
     * Seitenleisten-Eintrag bleibt.
     *
     * ⚠️ Die Vertiefung selbst bleibt unangetastet: sie ist ein Gespraech
     * und laeuft im Blatt weiter. Nur das Hineinrufen in den Chat faellt.
     */
    void today;
  } catch {
    /* still — die Karte im Verlauf existiert weiterhin */
  }
}

/**
 * Die offene Frage direkt oeffnen — ohne Umweg ueber Banner oder Chat.
 *
 * Martin: *„wenn dort angezeigt wird, eine Frage wartet auf dich, dass ich
 * direkt dorthin klicken kann [...] vielleicht, dass man sie überhaupt dort
 * standardmäßig lässt und aus dem Chat herausnimmt, weil man sowieso immer
 * wieder ins Zuhause geht und dort die Dinge abarbeiten möchte."*
 *
 * ⚠️ Bewusst DIESELBE Quelle wie `checkDeepQuestion` (`/deep-questions/open`)
 * und derselbe Oeffner. Ein zweiter Weg zur selben Frage waere eine zweite
 * Wahrheit darueber, welche Frage gerade dran ist.
 *
 * Gibt `false` zurueck, wenn keine offen ist — dann soll der Aufrufer etwas
 * anderes tun, statt ein leeres Blatt zu zeigen.
 */
export async function oeffneOffeneFrage(): Promise<boolean> {
  try {
    const r = await fetch("/api/deep-questions/open", { credentials: "include" });
    if (!r.ok) return false;
    const j = (await r.json()) as { question?: DeepQuestionOpen | null };
    const q = j.question;
    if (!q?.id || !q.question) return false;
    openDeepQuestionSheet(q);
    return true;
  } catch {
    return false;
  }
}

/**
 * Die Antwort des Tages — Frage und Antwort zum Nachlesen. (06.09.2026,
 * Martin: „sodass ich mich über den Tag noch an das erinnere.") Dasselbe
 * Blatt wie die Frage selbst, nur ohne Eingabe: Kicker, Ebene, Frage, Text.
 * Gibt false zurück, wenn heute nichts beantwortet wurde.
 */
export async function zeigeAntwortDesTages(): Promise<boolean> {
  let zeilen: TiefeZeile[] = [];
  try {
    const r = await fetch("/api/deep-questions/history", { credentials: "include" });
    if (!r.ok) return false;
    const j = (await r.json()) as { threads?: TiefeFaden[] };
    zeilen = antwortenDesTages(j.threads ?? [], wienTagSchluessel());
  } catch {
    return false;
  }
  if (zeilen.length === 0) return false;
  document.querySelector(".deepq-sheet")?.remove();
  const el = document.createElement("div");
  el.className = "sheet sheet--modal deepq-sheet deepq-sheet--antwort";
  el.setAttribute("role", "dialog");
  el.setAttribute("aria-modal", "true");
  const backdrop = document.createElement("div");
  backdrop.className = "sheet-backdrop";
  backdrop.addEventListener("click", () => el.remove());
  el.appendChild(backdrop);
  const card = document.createElement("div");
  card.className = "sheet-card deepq-sheet-card";
  const x = document.createElement("button");
  x.type = "button";
  x.className = "deepq-close";
  x.setAttribute("aria-label", "Schließen");
  x.textContent = "Schließen";
  x.addEventListener("click", () => el.remove());
  const kopf = document.createElement("header");
  kopf.className = "deepq-kopf";
  kopf.appendChild(x);
  const kickerZeile = document.createElement("div");
  kickerZeile.className = "deepq-kicker-zeile";
  const kicker = document.createElement("p");
  kicker.className = "deepq-kicker";
  kicker.textContent = zeilen.length === 1 ? "Deine Antwort heute" : `Dein Gang heute · ${zeilen.length} Ebenen`;
  kickerZeile.appendChild(kicker);
  kopf.appendChild(kickerZeile);
  card.appendChild(kopf);
  for (const z of zeilen) {
    const abschnitt = document.createElement("section");
    abschnitt.className = "deepq-antwort";
    const frage = document.createElement("h2");
    frage.className = "deepq-question";
    frage.textContent = z.question;
    const text = document.createElement("p");
    text.className = "deepq-antwort-text";
    text.textContent = z.answerText ?? "";
    abschnitt.append(frage, text);
    card.appendChild(abschnitt);
  }
  el.appendChild(card);
  document.body.appendChild(el);
  return true;
}

function openDeepQuestionSheet(q: DeepQuestionOpen): void {
  document.querySelector(".deepq-sheet")?.remove();
  const el = document.createElement("div");
  el.className = "sheet sheet--modal deepq-sheet";
  el.setAttribute("role", "dialog");
  el.setAttribute("aria-modal", "true");
  const backdrop = document.createElement("div");
  backdrop.className = "sheet-backdrop";
  backdrop.addEventListener("click", () => {
    closeDeepSheet();
  });
  el.appendChild(backdrop);
  // Ein Schliess-Pfad fuer alle Wege — beendet Diktat, Vorlesen und Dialog.
  const closeDeepSheet = (): void => {
    dqStopAudio();
    void dqStopDictation(false);
    el.remove();
  };

  const card = document.createElement("div");
  card.className = "sheet-card deepq-sheet-card";
  let setMicState = (_st: "idle" | "connecting" | "recording"): void => {};
  // 2026-07-21 (Martin, "spaeter weitermachen"): X schliesst NUR das
  // Sheet — die Frage bleibt offen, der Banner bleibt fuer spaeter
  // (kein Heute-nicht-Dismiss).
  const x = document.createElement("button");
  x.type = "button";
  x.className = "deepq-close";
  x.setAttribute("aria-label", "Schließen — später weitermachen");
  x.textContent = "Schließen";
  x.addEventListener("click", closeDeepSheet);

  const kopf = document.createElement("header");
  kopf.className = "deepq-kopf";
  kopf.appendChild(x);
  const kickerZeile = document.createElement("div");
  kickerZeile.className = "deepq-kicker-zeile";
  const kicker = document.createElement("p");
  kicker.className = "deepq-kicker";
  kicker.textContent = "Frage des Tages";
  kickerZeile.appendChild(kicker);
  const level = document.createElement("span");
  level.className = "deepq-level";
  kickerZeile.appendChild(level);
  kopf.appendChild(kickerZeile);
  card.appendChild(kopf);

  const p = document.createElement("h2");
  p.className = "deepq-question";
  card.appendChild(p);

  const ta = document.createElement("textarea");
  ta.className = "deepq-answer";
  ta.rows = 5;
  ta.placeholder = "Deine Antwort — so ehrlich, wie du magst …";
  card.appendChild(ta);
  // 2026-07-20 (Martin, "nach den Aufnahmen auf Enter druecken"): Enter
  // speichert, Shift+Enter macht den Zeilenumbruch.
  ta.addEventListener("keydown", (ev) => {
    if (ev.key === "Enter" && !ev.shiftKey) {
      ev.preventDefault();
      send.click();
    }
  });

  const row = document.createElement("div");
  row.className = "deepq-actions";

  // ── Whisper-Diktat (bewaehrter Weg) + Auto-Zuhoeren fuer den Dialog ──
  let dqRecorder: MediaRecorder | null = null;
  let dqStream: MediaStream | null = null;
  let dqChunks: Blob[] = [];
  let dqBusy = false;
  let dqHatDaten: () => boolean = () => false;
  let dqLive: LiveDictation | null = null;
  // Live-Diktat (#302 light): Feldstand VOR der Aufnahme + Intervall-Lauf.
  let dqBasisText = "";
  let dqLiveTimer: number | null = null;
  let dqLiveBusy = false;
  const dqMitBasis = (text: string): string =>
    dqBasisText ? dqBasisText.replace(/\s*$/, "") + " " + text : text;
  const dqLiveStop = (): void => {
    if (dqLiveTimer) {
      window.clearInterval(dqLiveTimer);
      dqLiveTimer = null;
    }
  };
  const dqLiveUpdate = async (mr: MediaRecorder): Promise<void> => {
    if (dqRecorder !== mr || dqLiveBusy || dqChunks.length === 0) return;
    dqLiveBusy = true;
    try {
      const type = mr.mimeType || "audio/webm";
      const ext = /mp4|mpeg|m4a/.test(type)
        ? "mp4"
        : /ogg/.test(type)
          ? "ogg"
          : "webm";
      const fd = new FormData();
      fd.append("audio", new Blob([...dqChunks], { type }), `live.${ext}`);
      const r = await fetch("/api/transcribe", {
        method: "POST",
        body: fd,
        credentials: "same-origin",
      });
      const j = (await r.json()) as { text?: string; echo?: boolean } | null;
      const text = (j?.text ?? "").trim();
      // Nur solange DIESE Aufnahme noch laeuft ins Feld schreiben.
      if (dqRecorder === mr && text && j?.echo !== true) {
        ta.value = dqMitBasis(text);
        syncZustand();
      }
    } catch {
      /* Live-Vorschau ist Kuer — der Fertig-Tipp transkribiert final */
    } finally {
      dqLiveBusy = false;
    }
  };
  const dqVizCtl = createMicViz();
  const dqCleanupStream = (): void => {
    dqVizCtl.stop();
    dqStream?.getTracks().forEach((t) => t.stop());
    dqStream = null;
    // Stabilisierung Teil 2: pausierte Tagesaufnahme wieder anwerfen.
    void import("./native-recorder.js")
      .then((m) => m.resumeDayRecorderAfterMic("tiefe-frage"))
      .catch(() => undefined);
  };
  const dqStopDictation = (commit: boolean): Promise<void> => {
    if (dqLive) {
      const live = dqLive;
      dqLive = null;
      setMicState("idle");
      // Verwerfen stellt das Feld wieder her — BEVOR Nachzuegler-Tokens
      // kaemen (der onText-Guard blockt sie ab jetzt ohnehin).
      if (!commit) ta.value = dqBasisText;
      syncZustand();
      void live.stop();
      // pre.3623: Der Live-Weg zeichnet je Bild (zeigeOhneEigeneMessung) —
      // ohne stop() liefe die Schleife bis zum nächsten Diktat weiter.
      dqVizCtl.stop();
      return Promise.resolve();
    }
    const mr = dqRecorder;
    dqRecorder = null;
    dqLiveStop();
    setMicState("idle");
    if (!mr) return Promise.resolve();
    return new Promise((resolve) => {
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      mr.addEventListener("stop", async () => {
        dqCleanupStream();
        // Safari/WKWebView liefert dataavailable teils erst NACH dem
        // stop-Event — nachfassen. Kamen noch NIE Daten (BT-Freisprecher
        // baut sekundenlang auf), deutlich laenger warten als sonst.
        const runden = dqHatDaten() ? 12 : 60;
        for (let i = 0; i < runden && commit && dqChunks.length === 0; i++) {
          await new Promise((r) => setTimeout(r, 50));
        }
        dqDebug(
          `[dq-mic] fix=1847 stop commit=${String(commit)} ` +
            `chunks=${dqChunks.length} bytes=${dqChunks.reduce((a, b) => a + b.size, 0)}`,
        );
        if (!commit || dqChunks.length === 0) {
          if (commit) {
            // Ehrlich statt still: der Nutzer hat "Fertig" getippt.
            reqDeps().showToast(
              "Nichts aufgenommen — bitte nochmal sprechen.",
              "error",
            );
          } else {
            // Verwerfen: die Live-Vorschau wieder aus dem Feld nehmen.
            ta.value = dqBasisText;
            syncZustand();
          }
          dqChunks = [];
          resolve();
          return;
        }
        const type = mr.mimeType || "audio/webm";
        const ext = /mp4|mpeg|m4a/.test(type)
          ? "mp4"
          : /ogg/.test(type)
            ? "ogg"
            : "webm";
        const fd = new FormData();
        fd.append("audio", new Blob(dqChunks, { type }), `antwort.${ext}`);
        dqChunks = [];
        dqBusy = true;
        send.disabled = true;
        const prevPh = ta.placeholder;
        ta.placeholder = "Wird übertragen …";
        fetch("/api/transcribe", {
          method: "POST",
          body: fd,
          credentials: "same-origin",
        })
          .then((r) => r.json())
          .then((j) => {
            const text = (
              ((j as { text?: string } | null)?.text ?? "")
            ).trim();
            if ((j as { echo?: boolean } | null)?.echo === true) {
              reqDeps().showToast(
                "Echo der Vorlese-Stimme erkannt — nicht übernommen.",
              );
            } else if (!text) {
              reqDeps().showToast(
                "Nichts verstanden — bitte nochmal sprechen.",
                "error",
              );
            } else if (text) {
              // Finale Transkription ersetzt die Live-Vorschau (Basis =
              // Feldstand VOR der Aufnahme, nicht der Live-Zwischenstand).
              ta.value = dqMitBasis(text).trimStart();
              syncZustand();
            }
          })
          .catch(() => {
            reqDeps().showToast(
              "Übertragung fehlgeschlagen — bitte nochmal sprechen.",
              "error",
            );
          })
          .finally(() => {
            dqBusy = false;
            send.disabled = false;
            ta.placeholder = prevPh;
            resolve();
          });
      });
      try {
        // Safari: letzten Datenblock aktiv anfordern, BEVOR gestoppt wird.
        try {
          mr.requestData();
        } catch {
          /* optional */
        }
        mr.stop();
      } catch {
        dqCleanupStream();
        resolve();
      }
    });
  };
  // 2026-07-20 (Martin, "das Stoppen moechte ich selber festlegen"):
  // KEINE Stille-Automatik mehr. Das Mikro startet im Dialog automatisch,
  // aber es laeuft, bis DU aufs Mikro tippst (= Fertig, Text bleibt) oder
  // "Verwerfen" drueckst (stoppt ohne Text). Absenden immer manuell.
  const dqStartRecording = async (): Promise<boolean> => {
    // SOFORT sichtbares Feedback beim Tipp — der Mikro-AUFBAU dauert
    // gemessen 1,3-5,4s (iPhone vs. Auto-BT); solange pulsiert der Knopf.
    setMicState("connecting");
    // ECHTES Streaming (Soniox, Martins Aufladung 22.07.): Wort fuer Wort
    // ins Feld — wie eine Diktat-Tastatur. Faellt beim Start-Fehler still
    // auf den Whisper-Intervall-Weg (unten) zurueck.
    try {
      const basis = ta.value;
      let ersterText = false;
      const startAt = Date.now();
      const live = new LiveDictation({
        onReady: () => {
          if (dqLive !== live) return;
          // JETZT sprechen — Leitung offen, nichts geht mehr verloren.
          setMicState("recording");
          hapticTap(15);
          dqDebug(`[dq-mic] soniox bereit nach ${Date.now() - startAt}ms`);
        },
        onText: (fin, interim) => {
          if (dqLive !== live) return;
          const t = (fin + (interim ? " " + interim : "")).trim();
          if (t && !ersterText) {
            ersterText = true;
            dqDebug("[dq-mic] soniox: erster text");
          }
          if (t) {
            ta.value = basis ? basis.replace(/\s*$/, "") + " " + t : t;
            syncZustand();
            // Beim Sprechen mitscrollen — man sieht, was erkannt wurde.
            ta.scrollTop = ta.scrollHeight;
          }
        },
        onError: (msg) => {
          dqDebug(`[dq-mic] soniox-fehler: ${msg}`);
          if (dqLive === live) {
            reqDeps().showToast(`Live-Diktat unterbrochen: ${msg}`, "error");
          }
        },
        onLevel: (p) => dqVizCtl.pushLevel(p),
      });
      await live.start();
      dqVizCtl.zeigeOhneEigeneMessung();
      dqLive = live;
      dqBasisText = basis;
      if (mic.getAttribute("data-state") !== "recording") {
        setMicState("connecting");
      }
      mic.title = "Fertig — tippen, wenn du ausgesprochen hast";
      discard.hidden = false;
      dqDebug("[dq-mic] soniox: gestartet");
      return true;
    } catch (err) {
      dqDebug(
        `[dq-mic] soniox-start fehlgeschlagen -> whisper-weg: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
    try {
      // EINE PTT-Leitung fuer alle Aufnahme-Wege (warm uebernehmen oder
      // Single-Flight mit Geraete-Wahl) — Martins Architektur-Ansage.
      dqStream = await acquireDictationStream();
      dqChunks = [];
      const mr = new MediaRecorder(dqStream);
      // BT-Freisprecher (z.B. Auto) brauchen SEKUNDEN bis zum ersten
      // Daten-Chunk — bis dahin zeigt der Knopf "verbindet", die Haptik
      // kommt erst mit den ersten echten Daten (= jetzt sprechen).
      let ersteDatenAt = 0;
      const startAt = Date.now();
      mr.addEventListener("dataavailable", (ev) => {
        if (ev.data && ev.data.size > 0) {
          if (ersteDatenAt === 0) {
            ersteDatenAt = Date.now();
            setMicState("recording");
            hapticTap(15);
            dqDebug(
              `[dq-mic] erste daten nach ${ersteDatenAt - startAt}ms`,
            );
          }
          dqChunks.push(ev.data);
        }
      });
      // Watchdog: liefert das Geraet nach 6s NICHTS, ehrlich sagen WELCHES.
      const geraet = dqStream.getAudioTracks()[0]?.label || "Mikrofon";
      window.setTimeout(() => {
        if (dqRecorder === mr && ersteDatenAt === 0) {
          reqDeps().showToast(
            `„${geraet}" liefert keine Daten — sprich weiter oder trenne Bluetooth und nutze das iPhone-Mikro.`,
            "error",
          );
          dqDebug(`[dq-mic] watchdog: 6s ohne daten (${geraet})`);
        }
      }, 6000);
      dqHatDaten = (): boolean => ersteDatenAt > 0;
      dqRecorder = mr;
      // 250ms-Zeitscheiben: im iOS-WebView kommt dataavailable sonst
      // erst NACH dem stop-Event -> Stop sah leere Chunks, stilles Nichts.
      mr.start(250);
      setMicState("connecting");
      // Live-Diktat: alle 2,5s das bisher Gesprochene zeigen.
      dqBasisText = ta.value;
      dqLiveStop();
      dqLiveTimer = window.setInterval(() => {
        void dqLiveUpdate(mr);
      }, 2500);
      const tr = dqStream.getAudioTracks()[0];
      dqDebug(
        `[dq-mic] fix=1847 start state=${mr.state} mime=${mr.mimeType || "?"} ` +
          `muted=${tr ? String(tr.muted) : "kein-track"} ` +
          `enabled=${tr ? String(tr.enabled) : "-"} label="${tr?.label ?? ""}"`,
      );
      tr?.addEventListener("mute", () => dqDebug("[dq-mic] track MUTED"));
      tr?.addEventListener("unmute", () => dqDebug("[dq-mic] track unmuted"));
      mic.title = "Fertig — tippen, wenn du ausgesprochen hast";
      discard.hidden = false;
      // Live-Pegel (geteiltes Modul mic-visualizer, auch Composer).
      dqVizCtl.start(dqStream);
      return true;
    } catch (err) {
      dqCleanupStream();
      setMicState("idle");
      reqDeps().showToast(
        "Mikrofon nicht möglich: " +
          (err instanceof Error ? err.message : String(err)),
        "error",
      );
      return false;
    }
  };
  const mic = document.createElement("button");
  mic.type = "button";
  mic.className = "deepq-mic";
  mic.setAttribute("aria-label", "Antwort sprechen");
  mic.title = "Antwort sprechen — tippen zum Starten und Beenden";
  mic.innerHTML =
    '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path fill="currentColor" d="M12 14a3 3 0 0 0 3-3V5a3 3 0 1 0-6 0v6a3 3 0 0 0 3 3zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.92V21h2v-3.08A7 7 0 0 0 19 11z" /></svg>';
  const micWort = document.createElement("span");
  micWort.className = "deepq-mic-wort";
  mic.appendChild(micWort);
  setMicState = (st) => {
    mic.setAttribute("data-state", st);
    el.classList.toggle("nimmt-auf", st !== "idle");
    micWort.textContent =
      st === "recording"
        ? "Fertig"
        : st === "connecting"
          ? "Wartet …"
          : "Sprechen";
  };
  setMicState("idle");
  // Verwerfen: waehrend der Aufnahme ein Wort unter Fertig — kein X
  // neben einer Pegel-Blase.
  const discard = document.createElement("button");
  discard.type = "button";
  discard.className = "deepq-verwerfen";
  discard.hidden = true;
  discard.textContent = "Verwerfen";
  discard.setAttribute("aria-label", "Aufnahme anhalten, ohne zu senden");
  discard.title = "Anhalten ohne Senden";
  const dqAfterStop = (): void => {
    mic.title = "Antwort sprechen — tippen zum Starten und Beenden";
    discard.hidden = true;
    setMicState("idle");
  };
  const dqMicTipp = (): void => {
    // 2026-07-22 (Martin): fuehlbare Bestaetigung am Mikro.
    hapticTap(dqRecorder || dqLive ? 25 : 15);
    if (dqRecorder || dqLive) {
      /*
       * 2026-09-03 (Martin: "wenn der Aufnahmeknopf aktiviert ist, moechte
       * ich beim erneuten Druecken gleich absenden — ich spreche, druecke,
       * das soll absenden, dann wird das naechste geladen und vorgelesen").
       *
       * Vorher hielt der zweite Druck nur an; abgeschickt wurde erst ueber
       * den Absenden-Knopf. Im Sprechfluss ist das ein Griff zu viel.
       *
       * ⚠️ Der Weg ohne Senden bleibt: der zweite Knopf darunter haelt an
       * und laesst den Text stehen, damit man ihn noch aendern kann.
       */
      void dqStopDictation(true).then(() => {
        dqAfterStop();
        if (ta.value.trim()) send.click();
      });
      return;
    }
    if (dqBusy) return;
    dqVizCtl.prepare();
    void dqStartRecording();
  };
  mic.addEventListener("click", dqMicTipp);
  discard.addEventListener("click", () => {
    if (!dqRecorder && !dqLive) return;
    // 2026-09-03: haelt an und BEHAELT den Text — der Weg, wenn man vor dem
    // Absenden noch etwas aendern will. Verwerfen war zu grob: man verlor
    // das Gesagte, obwohl man es nur nicht sofort schicken wollte.
    void dqStopDictation(true).then(dqAfterStop);
    reqDeps().showToast("Aufnahme angehalten — Text bleibt stehen.");
  });
  /*
   * ── Text leeren (11.09.2026) ──────────────────────────────────────────
   *
   * Martin, nach einer Aufnahme, in der eine Hintergrund-Unterhaltung mit
   * aufgezeichnet wurde: *„Ich konnte es dann aber danach nicht löschen.
   * Das wäre wichtig, dass ich Texte auch noch nachhinein anpassen kann
   * oder zumindest verwerfen, dass ich noch einmal spreche und neu
   * aufnehmen kann."*
   *
   * Tippen ins Textfeld ging technisch schon immer (`ta` ist ein normales
   * Textfeld) — aber unauffindbar, und einen ganzen verunglückten Absatz
   * per Tastatur zu markieren und zu löschen ist am Telefon muehsam. Und
   * selbst wer das schafft: `dqStartRecording` nimmt `ta.value` als BASIS
   * und haengt die naechste Aufnahme nur AN — ein zweiter Versuch ohne
   * vorheriges Leeren traegt den Hintergrund-Text also weiter mit.
   *
   * `discard` (Verwerfen) hilft hier nicht: Es existiert nur WAEHREND der
   * Aufnahme (`.nimmt-auf`) und haelt bewusst den Text — das ist die
   * richtige Regel fuer diesen anderen Fall (09.03.). Hier geht es um
   * DANACH, wenn schon etwas Falsches dasteht.
   */
  const leeren = document.createElement("button");
  leeren.type = "button";
  leeren.className = "deepq-leeren";
  leeren.textContent = "Text leeren";
  leeren.setAttribute("aria-label", "Antwort-Text leeren und neu beginnen");
  leeren.title = "Text leeren — neu sprechen oder schreiben";
  leeren.addEventListener("click", () => {
    ta.value = "";
    syncZustand();
    reqDeps().showToast("Text geleert — sprich oder schreib neu.");
  });
  const recGroup = document.createElement("div");
  recGroup.className = "deepq-rec-group";
  recGroup.appendChild(mic);

  /*
   * ── Journalblatt, nicht Aufnahme-Studio (2026-09-01) ─────────────────
   *
   * Martin: *„Die ganze Seite so scheiße ausschaut [...] Die tiefen Fragen
   * passen nicht einmal ansatzweise."*
   *
   * Der 30.08. hat das Mikrofon zum Helden gemacht: 104-Pixel-Kreis in der
   * Leere, die Frage klein oben. Vespera spricht aber in Display-Serif —
   * die FRAGE ist der Held, wie der Gruss auf Zuhause. Sprechen bleibt der
   * Hauptweg, als beschrifteter Ember-Knopf in der Daumenzone.
   *
   * Sobald Text da ist — und die Erkennung schreibt ihn mit
   * (`ta.value = dqMitBasis(text)`) — wird der TEXT zur Hauptsache.
   *
   * ⚠️ Der Zustand steht als Klasse am Blatt (`.deepq-sheet`). An der
   * inneren Karte greift keine Regel — dann bleibt das Feld unsichtbar.
   */
  const sprich = document.createElement("p");
  sprich.className = "deepq-sprich";
  sprich.textContent = "Antworte einfach laut";
  sprich.setAttribute("role", "button");
  sprich.tabIndex = 0;
  sprich.hidden = true;
  sprich.addEventListener("click", dqMicTipp);
  sprich.addEventListener("keydown", (e: KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      dqMicTipp();
    }
  });

  const schreiben = document.createElement("button");
  schreiben.type = "button";
  schreiben.className = "deepq-schreiben";
  schreiben.textContent = "oder schreiben";
  schreiben.addEventListener("click", () => {
    // Zustand haengt am SHEET (el.deepq-sheet), nicht an der Karte —
    // sonst greift keine CSS-Regel, das Feld bleibt unsichtbar, und
    // „oder schreiben" tut nichts.
    el.classList.add("schreibt");
    ta.focus();
  });

  /**
   * Hat das Blatt Text? Danach richtet sich die ganze Gestalt.
   *
   * ⚠️ An JEDER Stelle aufrufen, die `ta.value` setzt — sonst bleibt das
   * Blatt im falschen Zustand stehen, waehrend der Text schon da ist.
   */
  const syncZustand = (): void => {
    el.classList.toggle("hat-text", ta.value.trim().length > 0);
  };
  ta.addEventListener("input", syncZustand);

  const later = document.createElement("button");
  later.type = "button";
  later.className = "btn btn--sm deepq-later";

  const send = document.createElement("button");
  send.type = "button";
  send.className = "btn btn--primary btn--sm";

  /*
   * 2026-09-03 (Martin: "der Vorlesen-Knopf, dass der vielleicht als
   * Lautsprecher dargestellt wird statt Vorlesen"). Das Wort stand als
   * nackter Text ueber dem Sprechen-Knopf und sah aus wie eine Ueberschrift,
   * nicht wie etwas Antippbares. Ein Zeichen sagt dasselbe in einem Viertel
   * des Platzes — und die Bedeutung bleibt im aria-label.
   */
  const vorlesen = document.createElement("button");
  vorlesen.type = "button";
  vorlesen.className = "deepq-vorlesen";
  vorlesen.setAttribute("aria-label", "Frage vorlesen");
  vorlesen.title = "Frage vorlesen";
  vorlesen.innerHTML = zeichen("klang");

  const haupt = document.createElement("div");
  haupt.className = "deepq-haupt";
  haupt.appendChild(recGroup);
  haupt.appendChild(send);

  row.appendChild(vorlesen);
  row.appendChild(haupt);
  row.appendChild(schreiben);
  row.appendChild(discard);
  row.appendChild(leeren);
  row.appendChild(later);

  card.appendChild(row);
  el.appendChild(card);

  // ── Vorlesen (Bot-Stimme aus den Einstellungen) ──────────────────────
  let dqAudio: HTMLAudioElement | null = null;
  let dqAudioUrl: string | null = null;
  let vorleseRunde = 0;
  const setVorlesenUi = (an: boolean): void => {
    vorlesen.classList.toggle("is-on", an);
    vorlesen.innerHTML = zeichen(an ? "laeuft" : "klang");
    vorlesen.setAttribute(
      "aria-label",
      an ? "Vorlesen stoppen" : "Frage vorlesen",
    );
    vorlesen.title = an ? "Vorlesen stoppen" : "Frage vorlesen";
    vorlesen.classList.toggle("is-laeuft", an);
  };
  const dqStopAudio = (): void => {
    vorleseRunde++;
    try {
      dqAudio?.pause();
    } catch {
      /* egal */
    }
    dqAudio = null;
    if (dqAudioUrl) {
      URL.revokeObjectURL(dqAudioUrl);
      dqAudioUrl = null;
    }
    setVorlesenUi(false);
  };
  /**
   * Den Ton für einen Satz holen — EIN Weg für Vorwärmen und Abspielen.
   *
   * ⚠️ Bewusst derselbe Aufruf. Ein zweiter, „schnellerer" Weg fürs
   *    Vorwärmen wäre die nächste Stelle, an der zwei Fassungen
   *    auseinanderlaufen — und die Vertonung landet dann unter einem
   *    anderen Schlüssel im Zwischenspeicher als die, die gespielt wird.
   */
  const holeTon = async (text: string): Promise<Blob | null> => {
    const st = loadDeepqSettings();
    const body: Record<string, unknown> = { voiceId: st.voiceId, text };
    if (st.ttsProvider && st.ttsProvider !== "auto") {
      body.ttsProvider = st.ttsProvider;
    }
    if (st.voiceId.startsWith("de-DE-Chirp3-HD-")) {
      body.ttsProvider = "google";
      body.geminiVoice = st.voiceId.slice("de-DE-Chirp3-HD-".length);
    }
    const r = await fetch("/api/voice-preview", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify(body),
    });
    return r.ok ? await r.blob() : null;
  };

  /**
   * Der schon geholte Ton — vorgewärmt, während Martin die Frage liest.
   *
   * ## Woher das kommt
   *
   * Martin: *„Bitte nachsehen, warum es so lange dauert, bis er vorliest,
   * obwohl der Text schon mindestens fünf Sekunden dasteht."*
   *
   * GEMESSEN an 22 Vertonungen der letzten drei Stunden: **im Mittel 6,5
   * Sekunden, längste 8,8.** Das ist keine Schwäche des Weges, sondern der
   * Preis von Soniox: rund fünf Sekunden Grundkosten plus 0,056 je Zeichen.
   *
   * ⚠️ Deshalb wird nicht optimiert, sondern VORGEZOGEN. Die Frage steht auf
   *    dem Schirm, und Martin liest sie — genau die Sekunden, die die
   *    Vertonung braucht. Wer dann auf Vorlesen tippt, hört sofort.
   */
  let vorgewaermt: { text: string; blob: Blob } | null = null;
  let waermtGerade = "";
  const waermeFrage = (text: string): void => {
    const t = (text ?? "").trim();
    if (!t || vorgewaermt?.text === t || waermtGerade === t) return;
    waermtGerade = t;
    void holeTon(t)
      .then((blob) => {
        if (blob) vorgewaermt = { text: t, blob };
      })
      .catch(() => undefined)
      .finally(() => {
        if (waermtGerade === t) waermtGerade = "";
      });
  };

  const sprichFrage = async (text: string): Promise<void> => {
    const runde = ++vorleseRunde;
    try {
      dqAudio?.pause();
    } catch {
      /* egal */
    }
    setVorlesenUi(true);
    // Den A2DP-Umschlag schon beim Wunsch anstossen, nicht erst beim Ton —
    // sonst klingt der erste Moment blechern (gleiches Muster wie ueberall).
    prewarmHiFi();
    try {
      // Liegt der Ton schon vom Vorwärmen da, geht es ohne eine Sekunde
      // Wartezeit los — siehe vorgewaermt.
      const fertig =
        vorgewaermt?.text === text.trim() ? vorgewaermt.blob : null;
      const blob = fertig ?? (await holeTon(text));
      if (runde !== vorleseRunde) return;
      if (!blob) {
        setVorlesenUi(false);
        reqDeps().showToast("Vorlesen gerade nicht möglich.", "error");
        return;
      }
      dqAudioUrl = URL.createObjectURL(blob);
      dqAudio = new Audio(dqAudioUrl);
      /*
       * ⚠️ 2026-09-03 (Martin: "das Vorlesen ist viel leiser als wenn ich mit
       * dem Chat kommuniziere, das muss angeglichen werden").
       *
       * Serverseitig gemessen sind beide Wege gleich laut (-12,6 gegen
       * -12,1 dB bei demselben Satz) — der Unterschied entstand erst beim
       * Abspielen. Dies war der EINZIGE Abspielweg der App, der sich nicht
       * beim gemeinsamen Audio-System anmeldet; alle anderen (Vorlesen,
       * Musik, Aufnahmen) tun es. Ohne Anmeldung bleibt die iOS-Session dort,
       * wo sie gerade steht — im Zweifel im leisen Telefonprofil.
       */
      hookHiFiPlayback(dqAudio);
      await new Promise<void>((resolve) => {
        if (!dqAudio) {
          resolve();
          return;
        }
        dqAudio.addEventListener("ended", () => resolve());
        dqAudio.addEventListener("error", () => resolve());
        void dqAudio.play().catch(() => resolve());
      });
      if (runde === vorleseRunde) setVorlesenUi(false);
    } catch {
      if (runde === vorleseRunde) {
        setVorlesenUi(false);
        reqDeps().showToast("Vorlesen gerade nicht möglich.", "error");
      }
    }
  };
  vorlesen.addEventListener("pointerdown", () => unlockAudio());
  vorlesen.addEventListener("click", () => {
    hapticTap(15);
    unlockAudio();
    /*
     * ⚠️ 2026-09-03 (Martin: "ich kann das Vorlesen einschalten, aber wenn
     * ich es ausschalten moechte, geht es nicht"):
     *
     * Hier stand nur `if (dqAudio)`. Das Audio-Objekt entsteht aber ERST nach
     * der Synthese — bei frischem Text sind das rund zwoelf Sekunden. In
     * dieser ganzen Zeit war der Knopf nicht "aus", sondern startete einen
     * ZWEITEN Lauf.
     *
     * `vorleseRunde` hochzaehlen entwertet die laufende Anfrage (jeder
     * Rueckweg prueft `runde !== vorleseRunde`), das Audio wird gestoppt,
     * falls es schon da ist. Ein Aus-Knopf muss in JEDEM Zustand aus heissen.
     */
    if (dqAudio || vorlesen.classList.contains("is-laeuft")) {
      vorleseRunde++;
      dqStopAudio();
      setVorlesenUi(false);
      return;
    }
    if (dqRecorder || dqLive) void dqStopDictation(true);
    void sprichFrage(current.question);
  });

  let current = q;
  let ebene = (q.depth ?? 0) + 1;

  /**
   * Der Gang ist angekommen — ein Satz statt der naechsten Frage.
   *
   * Die Bedienung faellt weg: kein Mikro, kein Absenden, kein „Hier ist
   * gut". Es gibt nichts mehr zu antworten, und ein Knopf, der ins Leere
   * fuehrt, macht aus einem Abschluss eine offene Tuer.
   */
  const zeigeAbschluss = (satz: string): void => {
    kicker.textContent = "Angekommen";
    level.textContent = "";
    p.textContent = satz;
    p.classList.add("deepq-abschluss");
    el.classList.remove("schreibt");
    haupt.hidden = true;
    ta.hidden = true;
    later.textContent = "Schließen";
    later.disabled = false;
    later.onclick = () => closeDeepSheet();
    void sprichFrage(satz);
  };

  const renderEbene = (): void => {
    p.textContent = current.question;
    /*
     * ⚠️ Hier, nicht beim Klick. Die Vertonung braucht gemessen 6,5 Sekunden
     *    (22 Laeufe, laengste 8,8) — genau die Zeit, die Martin braucht, um
     *    die Frage zu lesen. Wer sie erst beim Tippen anstoesst, verschenkt
     *    sie. Siehe waermeFrage.
     */
    waermeFrage(current.question);
    level.textContent = ebene > 1 ? ` · Ebene ${ebene}` : "";
    later.textContent = ebene > 1 ? "Hier ist gut" : "Heute nicht";
    later.disabled = false;
    ta.value = "";
    el.classList.remove("schreibt");
    syncZustand();
    ta.disabled = false;
    send.disabled = false;
    send.textContent = "Antwort absenden";
    dqStopAudio();
  };

  later.addEventListener("click", () => {
    dqStopAudio();
    if (ebene > 1) {
      // Landung: bewusst beendet — kein Ausweichen. Best-effort melden.
      void fetch(
        `/api/deep-questions/${encodeURIComponent(current.id)}/land`,
        { method: "POST", credentials: "include" },
      ).catch(() => {});
      closeDeepSheet();
      const n = ebene - 1;
      reqDeps().showToast(
        `Gelandet — ${n} Ebene${n === 1 ? "" : "n"} tief. Danke für den Gang.`,
      );
    } else {
      const today = new Date().toISOString().slice(0, 10);
      localStorage.setItem(DEEPQ_DISMISS_KEY, `${current.id}|${today}`);
      closeDeepSheet();
    }
  });

  send.addEventListener("pointerdown", () => unlockAudio());
  send.addEventListener("click", () => {
    unlockAudio();
    void (async () => {
      // Laufende Aufnahme erst uebernehmen (wartet auf die Übertragung) —
      // sonst fehlt der gesprochene Text.
      if (dqRecorder || dqLive) await dqStopDictation(true);
      if (dqBusy) return;
      const text = ta.value.trim();
      if (!text) {
        ta.focus();
        return;
      }
      send.disabled = true;
      later.disabled = true;
      ta.disabled = true;
      try {
        const r = await fetch(
          `/api/deep-questions/${encodeURIComponent(current.id)}/answer`,
          {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ text }),
          },
        );
        if (!r.ok) throw new Error(String(r.status));
        // Folge-Frage sofort im Hintergrund vorbereiten (Tempo).
        holeDeeper(current.id);
        // 06.09.: Das Zuhause hakt „Frage" ab, ohne neu geladen zu werden.
        window.dispatchEvent(new CustomEvent("voice-v8:tiefe-frage-beantwortet"));
      } catch {
        send.disabled = false;
        later.disabled = false;
        ta.disabled = false;
        reqDeps().showToast(
          "Speichern fehlgeschlagen — bitte nochmal versuchen.",
          "error",
        );
        return;
      }
      // Antwort ist sicher — jetzt die naechste Ebene erfragen.
      send.textContent = "Die nächste Frage entsteht …";
      let next: DeepQuestionOpen | null = null;
      /*
       * ⚠️ 2026-08-30 — hier stand ein EIGENER fetch auf dieselbe Adresse.
       *
       * `holeDeeper()` zwanzig Zeilen weiter oben hat eine Sperre gegen
       * Doppelaufrufe (eine laufende Anfrage je Frage-Kennung). Dieser
       * Aufruf ging daran vorbei. Ergebnis, in der Datenbank gezaehlt: an
       * JEDER der 16 Ebenen von Martins Gang am 30.08. steht ein
       * Zwillingspaar — gleicher Elternteil, gleiche Tiefe, gleiche
       * Sekunde. Eine wurde beantwortet, die andere blieb fuer immer offen.
       *
       * Das ist kein Wettlauf-Zufall: die Fragen-Erzeugung dauert Sekunden,
       * also ueberlappen sich die beiden Aufrufe zuverlaessig — 16 von 16.
       *
       * Eine Tuer, eine Sperre. Der Server hat inzwischen zusaetzlich einen
       * eigenen Riegel; beides zusammen, weil ein zweiter Client, ein
       * Doppeltipp oder ein Wiederholungsversuch denselben Weg nehmen kann.
       */
      const j = await holeDeeper(current.id);
      next = (j?.question as DeepQuestionOpen | null) ?? null;
      if (next?.id && next.question) {
        current = next;
        ebene += 1;
        renderEbene();
        // Absenden IST die Geste — iOS erlaubt den Klang, wenn unlock
        // oben im Tipp saß. Nicht erst Vorlesen tippen müssen.
        void sprichFrage(current.question);
      } else if (j?.abschluss) {
        /*
         * ⚠️ 2026-09-03 (Martin: *„sollte es nicht einen Moment geben, wo
         * das System erkannt hat, dass ich am Ziel angekommen bin? Sonst
         * waeren es ewig tiefer werdende Fragen, die niemals enden."*)
         *
         * Der Gang endet nicht mit einem Rauswurf, sondern mit einem Satz.
         * Er bleibt stehen, bis Martin schliesst — deshalb kein
         * closeDeepSheet() hier: ein Schlusssatz, den man wegwischt, bevor
         * man ihn gelesen hat, ist keiner.
         */
        zeigeAbschluss(j.abschluss);
      } else {
        closeDeepSheet();
        reqDeps().showToast("Antwort gespeichert — sie formt die nächsten Fragen.");
      }
    })();
  });

  document.body.appendChild(el);
  renderEbene();
}
